import React, { FC } from "react";
import {
  faDiamondExclamation,
  faTriangle,
} from "@fortawesome/pro-regular-svg-icons";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { Box, HoverCard, Text, Tooltip } from "@mantine/core";

import { Reachability } from "@protos/sca/v1/sca";
import { formatTextAsMarkdown } from "@shared/utils";

import { InlineMarkdownCode } from "../InlineMarkdownCode";

import { CodeLocationIcon } from "./CodeLocationIcon";

type ReachabilityIconProps = {
  reachability: Reachability;
  reachableCondition?: string;
};

export const ReachabilityIcon: FC<ReachabilityIconProps> = ({
  reachability,
  reachableCondition,
}) => {
  // Reachable icon
  if (reachability === Reachability.REACHABLE) {
    return (
      <Tooltip
        label="Semgrep found a way to reach the vulnerability by scanning your code"
        position="bottom"
        withArrow={false}
        withinPortal
      >
        <Box>
          <CodeLocationIcon label={"Reachable"} icon={faTriangleExclamation} />
        </Box>
      </Tooltip>
    );
  }

  // Always Reachable Icon
  if (reachability === Reachability.ALWAYS_REACHABLE) {
    return (
      <Tooltip
        label="Semgrep identified the dependency itself as the vulnerability and recommends fixing it, regardless of its actual usage."
        position="bottom"
        withArrow={false}
        withinPortal
      >
        <Box>
          <CodeLocationIcon
            label={"Always Reachable"}
            icon={faTriangleExclamation}
          />
        </Box>
      </Tooltip>
    );
  }

  // Conditionally Reachable icon
  if (
    reachability === Reachability.CONDITIONALLY_REACHABLE &&
    reachableCondition
  ) {
    const formattedReachableCondition = formatTextAsMarkdown(
      `This vulnerability is reachable if ${reachableCondition}.`
    );
    return (
      <HoverCard withArrow withinPortal position="top" shadow="md">
        <HoverCard.Target>
          <Box>
            <CodeLocationIcon
              label="Conditionally Reachable"
              icon={faDiamondExclamation}
            />
          </Box>
        </HoverCard.Target>
        <HoverCard.Dropdown maw="30em">
          <Text fz="sm">
            <InlineMarkdownCode text={formattedReachableCondition} />
          </Text>
        </HoverCard.Dropdown>
      </HoverCard>
    );
  }

  // Unreachable icon
  if (reachability === Reachability.UNREACHABLE) {
    return (
      <Tooltip
        label="This finding is unreachable because Semgrep found no way to reach it by scanning your code."
        position="bottom"
        withArrow={false}
        withinPortal
      >
        <Box>
          <CodeLocationIcon label="Unreachable" icon={faTriangle} />
        </Box>
      </Tooltip>
    );
  }

  // Unknown Reachability icon
  if (reachability === Reachability.UNKNOWN) {
    return (
      <Tooltip
        label={
          <p style={{ whiteSpace: "break-spaces" }}>
            This finding requires a manual review because, at this moment, we do
            not have the rules or information available to assess its
            reachability.
          </p>
        }
        withArrow
        position="top"
        withinPortal
      >
        <Box>
          <CodeLocationIcon
            label="No reachability analysis"
            icon={faDiamondExclamation}
          />
        </Box>
      </Tooltip>
    );
  }

  return null;
};
